import { ApolloError } from '@apollo/client/index.js';
import { FieldErrors, FieldValues } from 'react-hook-form';

const SERVER_ERROR = 'root.serverError';

/**
 * Utility for determining if an error is a result of a record not being found.
 */
export function isApollo404Error(error: ApolloError) {
  return error.graphQLErrors.some(
    (err) => err.extensions?.code === 'not_found',
  );
}

/**
 * Utility for determining if an error is a result of the current user not being
 * allowed to access the record they asked for.
 *
 * Only a refusal of the operation's own field counts, hence the single-segment
 * path. A `forbidden` nested deeper in the response means the record itself is
 * accessible and something hanging off it isn't — a journey item's appointment
 * in another brand, say — which is not grounds for denying the whole page.
 */
export function isApolloForbiddenError(error: ApolloError) {
  return error.graphQLErrors.some(
    (err) => err.extensions?.code === 'forbidden' && err.path?.length === 1,
  );
}

/**
 * Utility for converting an error message to a server error type
 */
export const toServerError = (message?: string) =>
  [
    SERVER_ERROR,
    {
      type: 'server',
      message,
    },
  ] as const;

export const hasServerError = (errors?: FieldErrors<FieldValues>) => {
  const formHasServerError = errors?.root?.serverError !== undefined;
  return formHasServerError;
};

export const hasOnlyServerError = (errors: FieldErrors<FieldValues> = {}) => {
  const errorFieldsNames = Object.keys(errors);
  return errorFieldsNames.length === 1 && hasServerError(errors);
};
